import React, { useRef, useState } from 'react';

import { forceRefresh, ThumbnailToken } from './signed-thumbnail.store';
import { useSignedThumbnailToken } from './signed-thumbnail.context';

export interface ThumbnailProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  /** File name — the 'f' query param */
  f: string,
  /** Thumbnail size — the 'w' query param (same values the apps send today) */
  w: string | number,
  /** Optional damage id — the 'd' query param */
  d?: string,
  /** Rendered while the token is loading or after a genuine load failure */
  fallback?: React.ReactNode,
  onLoadError?: () => void
}

function buildSrc(token: ThumbnailToken, p: Pick<ThumbnailProps, 'f' | 'w' | 'd'>, retry: boolean): string {
  const params = new URLSearchParams({ f: p.f, w: String(p.w) });
  if (p.d) params.set('d', p.d);
  params.set('exp', String(token.exp));
  params.set('scope', token.scope);
  params.set('sig', token.sig);
  if (retry) params.set('retry', '1'); // cache-bust so the browser refetches
  return `${token.baseUrl}?${params.toString()}`;
}

export function Thumbnail({ f, w, d, fallback = null, onLoadError, alt = '', ...imgProps }: ThumbnailProps) {
  const token = useSignedThumbnailToken();
  const retried = useRef(false);
  const [failed, setFailed] = useState(false);
  const [freshToken, setFreshToken] = useState<ThumbnailToken | null>(null);

  if (!token || failed) return <>{fallback}</>;

  // Check 2 (reactive): <img> onError can't see the HTTP status, so on ANY error
  // assume an expired token once — refresh (deduped) and retry with the new sig.
  // A second failure is a genuine error (404 etc.).
  const handleError = () => {
    if (retried.current) {
      setFailed(true);
      onLoadError?.();
      return;
    }
    retried.current = true;
    forceRefresh()
      .then(setFreshToken)
      .catch(() => {
        setFailed(true);
        onLoadError?.();
      });
  };

  const effectiveToken = freshToken ?? token;
  return (
    <img
      {...imgProps}
      alt={alt}
      src={buildSrc(effectiveToken, { f, w, d }, retried.current)}
      onError={handleError}
    />
  );
}
